import React from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import PageShell from "@/components/PageShell";
import { Globe, Star, MapPin, ArrowRight, Check } from "lucide-react";

const BANNER = "/mondo-banner.jpg";
const ITALY_IMG = "/mondo-tesori-plan.jpg";

const CaffeColaNelMondo = () => {
  const { t } = useTranslation();
  const values = t("mondo.values", { returnObjects: true }) || [];
  const cardIcons = { expo: Globe, brand: Star, kansai: MapPin };
  const cardKeys = ["expo", "brand", "kansai"];

  return (
    <PageShell>
      <SeoHead pageKey="mondo" image={BANNER} />
      {/* Clean image-only hero banner (no text overlay) */}
      <section
        className="relative isolate overflow-hidden"
        style={{ minHeight: "min(60vh, 520px)" }}
        data-testid="mondo-banner"
      >
        <img
          src={BANNER}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center center" }}
        />
      </section>

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="divider-beans font-serif-display uppercase tracking-[0.3em] text-xs mb-5">
            {t("mondo.introEyebrow")}
          </div>
          <h2 className="font-serif-display font-bold leading-tight" style={{ color: "#2a160c", fontSize: "clamp(1.9rem, 3.2vw, 2.8rem)" }}>
            {t("mondo.introTitle")}
          </h2>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "#3b2417" }} dangerouslySetInnerHTML={{ __html: t("mondo.introP") }} />
        </div>

        {/* 3-image gallery under the intro paragraph */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8 mt-12 sm:mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 items-stretch">
            {/* Bollino Brand Ambassador */}
            <div
              className="rounded-xl overflow-hidden flex items-center justify-center p-6"
              style={{
                background: "#fbf5e9",
                border: "1px solid rgba(91,54,33,0.12)",
                boxShadow: "0 20px 40px -25px rgba(42,22,12,0.4), 0 6px 12px -6px rgba(42,22,12,0.2)",
                aspectRatio: "1 / 1",
              }}
            >
              <img
                src="/mondo3-badge.png"
                alt="Brand Ambassador Tesori d'Italia — bollino ufficiale"
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>
            {/* Confezione Miscela Esclusiva */}
            <div
              className="rounded-xl overflow-hidden flex items-center justify-center"
              style={{
                background: "#fbf5e9",
                border: "1px solid rgba(91,54,33,0.12)",
                boxShadow: "0 20px 40px -25px rgba(42,22,12,0.4), 0 6px 12px -6px rgba(42,22,12,0.2)",
                aspectRatio: "1 / 1",
              }}
            >
              <img
                src="/mondo3-bag.jpg"
                alt="Caffè Cola per Tesori d'Italia — Miscela Esclusiva 1 kg"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
            {/* Composizione prodotti */}
            <div
              className="rounded-xl overflow-hidden"
              style={{
                border: "1px solid rgba(91,54,33,0.12)",
                boxShadow: "0 20px 40px -25px rgba(42,22,12,0.4), 0 6px 12px -6px rgba(42,22,12,0.2)",
                aspectRatio: "1 / 1",
              }}
            >
              <img
                src="/mondo3-boxes.jpg"
                alt="Caffè Cola for Tesori d'Italia — linea completa con box e cialde"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section-y relative overflow-hidden" style={{ background: "#2a160c", color: "#f5ead6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <div
              className="relative rounded-xl overflow-hidden"
              style={{
                background: "#0b1a3a",
                boxShadow: "0 30px 60px -25px rgba(0,0,0,0.5)",
                border: "1px solid rgba(245,234,214,0.08)",
              }}
            >
              <img
                src={ITALY_IMG}
                alt={t("mondo.italyAlt")}
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>{t("mondo.tesoriEyebrow")}</span>
            <h2 className="font-serif-display font-bold mt-3 leading-tight" style={{ fontSize: "clamp(1.7rem, 2.8vw, 2.4rem)" }}>
              {t("mondo.tesoriTitle")}
            </h2>
            <div className="mt-6 space-y-4 text-[15px] sm:text-base leading-relaxed" style={{ color: "#ecdcbd" }}>
              <p dangerouslySetInnerHTML={{ __html: t("mondo.tesoriP1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("mondo.tesoriP2") }} />
            </div>

            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: "#c5371f" }}>
                {t("mondo.tesoriValues")}
              </p>
              <div className="flex flex-wrap gap-2">
                {values.map((v, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[13px] font-medium"
                    style={{ background: "rgba(245,234,214,0.1)", border: "1px solid rgba(245,234,214,0.25)", color: "#f5ead6" }}
                  >
                    <Check size={13} style={{ color: "#c5371f" }} /> {v}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y" style={{ background: "#f5ead6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>{t("mondo.kansaiEyebrow")}</span>
            <h2 className="font-serif-display font-bold mt-3 leading-tight" style={{ color: "#2a160c", fontSize: "clamp(1.7rem, 2.8vw, 2.4rem)" }}>
              {t("mondo.kansaiTitle")}
            </h2>
            <div className="mt-6 space-y-4 text-[15px] sm:text-base leading-relaxed" style={{ color: "#3b2417" }}>
              <p dangerouslySetInnerHTML={{ __html: t("mondo.kansaiP1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("mondo.kansaiP2") }} />
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {cardKeys.map((k) => {
                const Icon = cardIcons[k];
                return (
                  <div key={k} className="rounded-2xl p-5" style={{ background: "#fbf5e9", border: "1px solid rgba(91,54,33,0.15)" }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: "#c5371f" }}>
                      <Icon size={18} color="#fff" />
                    </div>
                    <p className="font-serif-display font-bold" style={{ color: "#2a160c" }}>{t(`mondo.kansaiCards.${k}.title`)}</p>
                    <p className="text-xs uppercase tracking-wider mt-1" style={{ color: "#5b3621" }}>{t(`mondo.kansaiCards.${k}.desc`)}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            {/* Horizontal event photo — Axess Industry / Expo 2025 Osaka */}
            <div
              className="rounded-xl overflow-hidden"
              style={{
                boxShadow: "0 30px 60px -25px rgba(42,22,12,0.45), 0 10px 20px -8px rgba(42,22,12,0.25)",
                border: "1px solid rgba(91,54,33,0.12)",
              }}
            >
              <img
                src="/japan-event.jpg"
                alt="Evento istituzionale Axess Industry — Expo 2025 Osaka, presentazione Made in Italy in Giappone"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        <div className="text-center mt-14">
          <Link to="/nel-tempo" className="btn-red focus-ring inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold uppercase tracking-wider text-sm">
            {t("mondo.cta")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default CaffeColaNelMondo;
